import ExpandMore from '@mui/icons-material/ExpandMore'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import React, { createElement, forwardRef, useState, useEffect } from 'react'
import DatePicker from 'react-datepicker'
import { Box, ButtonBase, Typography } from '@mui/material'
import theme from 'lib/theme'
import { useLocale } from 'providers/LocaleProvider'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom'
import { DatePickerButton } from 'views/ServiceAreas/ServiceArea.styled'
import { CustomHeader } from './CustomHeader'
import 'react-datepicker/dist/react-datepicker.css'
import './DateRangePicker.css'

export const DateRangePicker = ({
  queries,
  dateRange,
}: {
  queries: { [key: string]: string } | null
  dateRange: string[]
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const locationState = location.state as { placeName: string }
  const [isOpen, setIsOpen] = useState(false)
  const [tempDateRange, setTempDateRange] = useState<
    [Date | null, Date | null]
  >([new Date(dateRange[0]), new Date(dateRange[1])])

  const searchParams = new URLSearchParams(location.search)

  const showPlaceHolder = searchParams.get('cleared') === 'true'

  const [tempStartDate, tempEndDate] = tempDateRange
  const { selectedLocale } = useLocale()
  const { t } = useTranslation()
  const placeholder = t('chooseDateRange')
  useEffect(() => {
    setTempDateRange([new Date(dateRange[0]), new Date(dateRange[1])])
  }, [dateRange])

  const handleSubmit = () => {
    const startDeliveryDate = format(tempDateRange[0] as Date, 'yyyy-MM-dd')
    const endDeliveryDate = format(
      tempDateRange[1] || (tempDateRange[0] as Date),
      'yyyy-MM-dd'
    )
    const formattedParams = createSearchParams({
      start_delivery_date: startDeliveryDate,
      end_delivery_date: endDeliveryDate,
      page: '1',
      ...queries,
    })
    setIsOpen(false)
    navigate(`/order?${formattedParams}`, {
      state: locationState,
    })
  }
  const handleClear = () => {
    const formattedParams = createSearchParams({
      page: '1',
      cleared: 'true',
      ...queries,
    })
    setTempDateRange([null, null])
    setIsOpen(false)
    navigate(`/order?${formattedParams}`, {
      state: locationState,
    })
  }

  const CustomInput = forwardRef<
    HTMLButtonElement,
    {
      value: string
      onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
    }
  >(({ value, onClick }, ref) => (
    <DatePickerButton
      ref={ref}
      variant='outlined'
      onClick={onClick}
      sx={{
        height: theme.spacing(5),
        padding: 0,
        '& .MuiButton-endIcon': { marginRight: theme.spacing(1) },
        '& .MuiButton-startIcon': {
          marginRight: 0,
          marginLeft: theme.spacing(1),
        },
        marginLeft: theme.spacing(1),
      }}
      startIcon={
        <AccessTimeIcon
          sx={{
            fill: theme.palette.text.secondary,
            '&:hover': { fill: theme.palette.text.secondary },
          }}
        />
      }
      endIcon={
        <ExpandMore
          sx={{
            fill: theme.palette.text.secondary,
          }}
        />
      }>
      <Typography
        variant='body2'
        sx={{ color: theme.palette.text.secondary, lineHeight: 'inherit' }}>
        {showPlaceHolder ? placeholder : value}
      </Typography>
    </DatePickerButton>
  ))
  return (
    <div style={{ zIndex: 4 }}>
      <DatePicker
        locale={selectedLocale}
        dateFormat='P'
        startDate={tempStartDate}
        endDate={tempEndDate}
        open={isOpen}
        onInputClick={() => {
          setIsOpen(true)
        }}
        onClickOutside={() => setIsOpen(false)}
        onChange={(update) => {
          if (update) {
            setTempDateRange(update)
          }
        }}
        useWeekdaysShort
        monthsShown={2}
        shouldCloseOnSelect={false}
        selectsRange
        customInput={createElement(CustomInput)}
        renderCustomHeader={({
          monthDate,
          customHeaderCount,
          decreaseMonth,
          increaseMonth,
        }) => (
          <CustomHeader
            monthDate={monthDate}
            customHeaderCount={customHeaderCount}
            decreaseMonth={decreaseMonth}
            increaseMonth={increaseMonth}
          />
        )}
        calendarClassName='date_picker_calendar'
        popperPlacement='bottom'
        showPopperArrow={false}>
        <Box
          sx={{
            display: 'flex',
            position: 'absolute',
            bottom: 0,
            left: 0,
            marginTop: theme.spacing(3),
            width: '100%',
            borderTop: `1px solid ${theme.palette.action.disabled}`,
          }}>
          <ButtonBase
            onClick={handleSubmit}
            sx={{ margin: theme.spacing(2), marginLeft: theme.spacing(4) }}>
            <Typography
              variant='body2'
              sx={{ color: theme.palette.primary.main, fontSize: '10px' }}>
              {t('ok')}
            </Typography>
          </ButtonBase>
          <ButtonBase
            sx={{ marginLeft: theme.spacing(3) }}
            onClick={() => {
              setTempDateRange([new Date(dateRange[0]), new Date(dateRange[1])])
              setIsOpen(false)
            }}>
            <Typography
              variant='body2'
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '10px',
              }}>
              {t('cancel')}
            </Typography>
          </ButtonBase>
          <ButtonBase
            sx={{ marginLeft: theme.spacing(4) }}
            onClick={handleClear}>
            <Typography
              variant='body2'
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '10px',
              }}>
              {t('clear')}
            </Typography>
          </ButtonBase>
        </Box>
      </DatePicker>
    </div>
  )
}
